import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { AdapterError, AdapterOptions, FlatAnswerMutation, SectionAdapter } from './types'

/**
 * Base abstract class for all base subsection adapters
 * Each subsection provides bidirectional transformation functions
 *
 * Form data is accessed through `getFormDataField()` and `validateFormDataField()`
 * which enforce validation. If a required field is missing, `getFormDataField()`
 * throws an error, causing the parent adapter to skip updating that section
 * entirely. This ensures missing data results in no update rather than
 * overwriting existing data with undefined/null values.
 */
export abstract class BaseSubsectionAdapter<T> extends SectionAdapter<T> {
  protected formData: T
  /**
   * Extract form data from flat FHIR answers for this subsection
   */
  abstract extractFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    options?: AdapterOptions
  ): Partial<T>

  /**
   * Build flat FHIR answers from form data for this subsection
   */
  abstract buildFlatAnswers(): FlatAnswerMutation[]

  public setFormData(formData: T): void {
    this.formData = formData
  }

  /**
   * Get a form data field by key. Throws if the field doesn't exist,
   * causing the parent adapter to skip updating this section.
   * This should be the ONLY way to access form data fields.
   */
  protected getFormDataField<K extends keyof T>(key: K): T[K] {
    if (Object.prototype.hasOwnProperty.call(this.formData, key)) {
      return this.formData[key]
    }
    throw new AdapterError(
      `Field ${String(key)} not found in form data for ${this.constructor.name}`,
      undefined,
      this.constructor.name
    )
  }

  /**
   * Check if a form data field exists without throwing.
   * Use this for optional fields. like Daily or Weekly schedules.
   */
  protected formDataFieldExists(key: string): boolean {
    if (Object.prototype.hasOwnProperty.call(this.formData, key)) {
      return true
    }
    return false
  }
}
